import { inject, injectable } from 'inversify'
import { PreloaderSettings } from '@/decorators/preloaderSettings'
import TicketCount from '@/data/models/domain/tickets/TicketCount'
import type { Preloadable, PreloaderType } from '@/services/preloaders/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type ModelPreloaderService from '@/services/preloaders/modelPreloaderService'
import type BaseModel from '@/data/models/BaseModel'
import { TmApiServerError } from '@/core/error/transport/tmApiServerError'
import type TicketCountService from '@/services/domain/tickets/ticketCountService'
import { ServiceGroups } from '@/config/types'

@PreloaderSettings({
  models: [TicketCount],
})
@injectable()
export class TicketCountPreloaderService implements Preloadable {
  @inject(SERVICE_TYPES.ModelPreloaderService) protected readonly modelPreloaderService: ModelPreloaderService
  @inject(SERVICE_TYPES.TicketCountService) protected readonly ticketCountService: TicketCountService

  public async preloadUnreadTicketsCount() {
    const key = this.getKey()

    if (this.modelPreloaderService.isPreloaded(key, ServiceGroups.PRELOADERS)) {
      return
    }

    this.modelPreloaderService.startPreloading(key, TicketCount)

    try {
      await this.ticketCountService.fetchUnreadTicketsCount()
      this.modelPreloaderService.markAsPreloaded(key)
    } catch (e) {
      if (e instanceof TmApiServerError) {
        this.modelPreloaderService.markAsFailed(key)
      }
      throw e
    }
  }

  public markUnreadTicketsCountAsNotPreloaded() {
    this.modelPreloaderService.markAsNotPreloaded(this.getKey())
  }

  public markUnreadTicketsCountAsPreloaded() {
    const key = this.getKey()
    if (this.modelPreloaderService.isPreloaded(key, ServiceGroups.PRELOADERS)) {
      return
    }
    this.createAndMarkAsPreloaded(key, TicketCount)
  }

  public isPreloaded(key: string, preloaderType: PreloaderType): boolean {
    return this.modelPreloaderService.isPreloaded(key, preloaderType)
  }

  public startPreloading(key: string, model: typeof BaseModel, preloaderType: PreloaderType) {
    return this.modelPreloaderService.startPreloading(key, model, preloaderType)
  }

  public markAsPreloaded(key: string): void {
    return this.modelPreloaderService.markAsPreloaded(key)
  }

  public markAsNotPreloaded(key: string): void {
    this.modelPreloaderService.markAsNotPreloaded(key)
  }

  public markAsFailed(key: string): void {
    this.modelPreloaderService.markAsFailed(key)
  }

  public preload(model: typeof BaseModel): Promise<BaseModel[]> {
    return this.modelPreloaderService.preload(model)
  }

  public preloadById(model: typeof BaseModel, id: string) {
    return this.modelPreloaderService.preloadById(model, id)
  }

  public reloadById(model: typeof BaseModel, id: string) {
    return this.modelPreloaderService.reloadById(model, id)
  }

  public isFailed(key: string, model: typeof BaseModel, preloaderType?: PreloaderType): boolean {
    return this.modelPreloaderService.isFailed(key, model, preloaderType)
  }

  public isFailedById(model: typeof BaseModel, id: string, preloaderType?: PreloaderType): boolean {
    return this.modelPreloaderService.isFailedById(model, id, preloaderType)
  }

  public createAndMarkAsPreloaded(key: string, model: typeof BaseModel): void {
    this.modelPreloaderService.createAndMarkAsPreloaded(key, model)
  }

  protected getKey() {
    return 'ticketUnreadCount'
  }
}
